import { getQueryParam } from '@/utils/route'

type AvailableLegislation = 'zertes' | 'eidas'
type QualityParam = 'qes' | 'aes' | ''
type LegislationParam = AvailableLegislation | ''

type QueryParams = {
  quality: QualityParam
  legislation: LegislationParam
  srId: string
}

export default function useIdentAdvancedAvailability() {
  const { t } = useI18n()
  const { currentPlatform } = usePlatformSwitcher()
  const { createSnackbar } = useWidgetStore()
  const userStore = useUserStore()
  const { signatureQualities } = storeToRefs(useUserStore())
  const { isZertesActiveForMembers, isEidasActiveForMembers, isIdentMethodNect } = storeToRefs(useBusinessStore())
  const identStore = useIdentStore()
  const { isDirectSign, auth, invitingBusinessId } = storeToRefs(useDirectSignStore())
  const { srRepository, directSrRepository } = useApi()
  const { getCurrentSigningDetails } = useSignatureRequestHelpers()
  const route = useRoute()

  const signatureRequest = ref<SignatureRequest | null>(null)

  const nasData = computed(() => {
    return {
      srId: auth.value.password,
      userId: auth.value.username,
    }
  })

  const queryParams = computed((): QueryParams => {
    return {
      quality: getQueryParam<QualityParam>(route.query.q),
      legislation: getQueryParam<LegislationParam>(route.query.l),
      srId: getQueryParam<string>(route.query.sr),
    }
  })

  const requestedQuality = computed((): QualityParam => {
    return queryParams.value.quality
  })
  const requestedLegislation = computed((): LegislationParam => {
    if (isDirectSign.value) {
      const legislation = signatureRequest.value?.legislation.toLowerCase()
      if (legislation === 'zertes' || legislation === 'eidas') {
        return legislation
      }
    }
    return queryParams.value.legislation
  })
  const signatureRequestId = computed(() => {
    return isDirectSign.value ? nasData.value.srId : queryParams.value.srId
  })

  const userHasQESZertes = computed(() => signatureQualities.value?.qes?.zertes)
  const userHasQESEidas = computed(() => signatureQualities.value?.qes?.eidas)

  const availableQESLegislation = computed((): AvailableLegislation => {
    if (!requestedLegislation.value) {
      // if ident method is nect, only eidas is available,
      // else, use the platform-dependent defaults
      if (isIdentMethodNect.value) {
        return 'eidas'
      }
      return currentPlatform.value === 'CH' ? 'zertes' : 'eidas'
    } else {
      return requestedLegislation.value === 'zertes' ? 'zertes' : 'eidas'
    }
  })

  const checkIsIdentificationCovered = async () => {
    if (signatureRequestId.value || requestedLegislation.value) {
      return await identStore.checkIsIdentificationCovered({
        srId: signatureRequestId.value,
        legislation: requestedLegislation.value || undefined,
      })
    }
    switch (availableQESLegislation.value) {
      case 'eidas':
        return isEidasActiveForMembers.value
      case 'zertes':
        return isZertesActiveForMembers.value
      default:
        return false
    }
  }

  const meetsQESRequirements = computed(() => {
    // if coming from View SR page, the requested quality is set in the query parameter
    // else if coming from Signature Standards page, the requested quality is empty
    if (['qes', ''].includes(requestedQuality.value.toLowerCase())) {
      const availableLegislation = availableQESLegislation.value

      switch (availableLegislation) {
        case 'eidas':
          return userHasQESEidas.value
        case 'zertes':
          return userHasQESZertes.value
        default:
          return false
      }
    }
    return true
  })

  const closeIdentFlow = () => {
    if (isDirectSign.value) {
      return navigateTo({
        name: 'view-id-userid',
        params: { id: nasData.value.srId, userId: nasData.value.userId },
      })
    } else if (signatureRequestId.value) {
      return navigateTo({
        name: 'view-srid',
        params: { srid: signatureRequestId.value },
      })
    }
    navigateTo('/profile/signature-standards')
  }

  const navigateBack = () => {
    if (isDirectSign.value) {
      return navigateTo({
        replace: true,
        path: `/direct-sign/identification/${nasData.value.srId}/${nasData.value.userId}`,
      })
    }
    navigateTo({
      name: 'profile-signature-standards-advanced-update',
      query: route.query,
    })
  }

  const handleIdentExists = () => {
    closeIdentFlow()
    showSuccessSnackbar()
  }

  const showSuccessSnackbar = () => {
    createSnackbar({
      message: signatureRequestId.value
        ? t('signature_standards.increase.advanced.update_success_sr')
        : t('signature_standards.increase.advanced.update_success'),
    })
  }

  const showErrorSnackbar = () => {
    createSnackbar({
      message: t('signature_standards.increase.advanced.update_error'),
    })
  }

  const checkNasIdentExists = async () => {
    if (!signatureRequest.value) {
      return false
    }
    const signingDetails = getCurrentSigningDetails(signatureRequest.value)
    return Boolean(signingDetails.quality)
  }

  const checkIdentExists = async () => {
    await userStore.fetchUser()
    let nasIdentExists = false
    if (isDirectSign.value) {
      nasIdentExists = await checkNasIdentExists()
    }
    if (meetsQESRequirements.value || nasIdentExists) {
      handleIdentExists()
      return true
    }
    return false
  }

  const fetchSignatureRequest = async () => {
    if (isDirectSign.value) {
      signatureRequest.value = await directSrRepository.get(nasData.value.srId)
      const srBusinessId = signatureRequest.value?.businessId
      if (srBusinessId) {
        invitingBusinessId.value = srBusinessId
      }
    } else {
      signatureRequest.value = await srRepository.get(signatureRequestId.value)
    }
  }

  onMounted(() => {
    if (signatureRequestId.value) {
      fetchSignatureRequest()
    }
  })

  return {
    checkIdentExists,
    requestedQuality,
    requestedLegislation,
    signatureRequestId,
    checkIsIdentificationCovered,
    closeIdentFlow,
    navigateBack,
    handleIdentExists,
    showSuccessSnackbar,
    showErrorSnackbar,
    signatureRequest,
    nasData,
    availableQESLegislation,
  }
}
